import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const TOKEN_KEY = "sensiiq_token";

async function bootstrapGuest() {
  const res = await axios.post(`${API}/auth/guest`, {});
  localStorage.setItem(TOKEN_KEY, res.data.access_token);
  return { token: res.data.access_token, user: res.data.user };
}

// Idempotent: ensures we have a valid token. Returns it.
export async function ensureAuthToken() {
  const existing = localStorage.getItem(TOKEN_KEY);
  if (existing) {
    try {
      await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${existing}` },
      });
      return existing;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    }
  }
  const { token } = await bootstrapGuest();
  return token;
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Modal control — any component can call openAuth({ mode: 'signup'|'signin', reason: '...' })
  const [authModal, setAuthModal] = useState({ open: false, mode: "signup", reason: "" });

  const refreshMe = useCallback(async (overrideToken) => {
    const t = overrideToken || localStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const t = await ensureAuthToken();
        setToken(t);
        await refreshMe(t);
      } catch (err) {
        console.error("Auth bootstrap failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshMe]);

  const setAuth = useCallback((newToken, newUser) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setToken(null);
    // Immediately bootstrap a fresh guest so the app keeps working
    try {
      const { token: gt, user: gu } = await bootstrapGuest();
      setToken(gt);
      setUser(gu);
    } catch {
      // ignore — next API call will retry
    }
  }, []);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const openAuth = useCallback((opts = {}) => {
    setAuthModal({ open: true, mode: opts.mode || "signup", reason: opts.reason || "" });
  }, []);
  const closeAuth = useCallback(() => setAuthModal((m) => ({ ...m, open: false })), []);

  return (
    <AuthContext.Provider value={{
      user, token, loading, logout, authHeaders, setAuth, refreshMe,
      authModal, openAuth, closeAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
